import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportsApi, templatesApi, clientsApi, voiceApi, photosApi, clockApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { LogoHome } from '../components/LogoHome';
import { PhotoThumbnail } from '../components/PhotoThumbnail';
import { normaliseImageForUpload } from '../utils/normaliseImage';
import { 
  Mic, MicOff, Loader2, ArrowLeft, Sparkles, Save, Send,
  CheckCircle2, AlertTriangle, FileText, Shield, Download, Mail, History, RotateCcw, X, Camera, ImageIcon
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function ReportWriter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [formData, setFormData] = useState({});
  const [missingFields, setMissingFields] = useState(new Set());
  const [rawNotes, setRawNotes] = useState('');
  const [report, setReport] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(false);
  const [diffVersion, setDiffVersion] = useState(null);
  const [savingPdf, setSavingPdf] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    loadInitialData();
  }, [id]);

  const loadInitialData = async () => {
    try {
      const [templatesRes, clientsRes, clockRes, latestRes] = await Promise.all([
        templatesApi.getAll(),
        clientsApi.getAll(),
        clockApi.getStatus().catch(() => ({ data: { clocked_in: false } })),
        clockApi.getLatestCompleted().catch(() => ({ data: {} })),
      ]);
      setTemplates(templatesRes.data);
      setClients(clientsRes.data);

      if (id && id !== 'new') {
        const reportRes = await reportsApi.getById(id);
        setReport(reportRes.data);
        setSelectedClient(reportRes.data.client_id);
        setFormData(reportRes.data.form_data || {});
        setRawNotes(reportRes.data.raw_notes || '');
        const template = templatesRes.data.find(t => t.id === reportRes.data.template_id);
        setSelectedTemplate(template);
      } else {
        // New report: prefill from active OR latest completed clock-in
        const active = clockRes.data?.active_record;
        const latest = latestRes.data || {};
        const trackedKm = typeof (active?.total_km) === 'number' ? active.total_km
                        : typeof (latest?.total_km) === 'number' ? latest.total_km
                        : null;
        const prefillClient = active?.client_id || latest?.client_id;
        if (prefillClient) setSelectedClient(prefillClient);

        const goals = active?.selected_goals || [];
        const goalUpdates = {};
        if (goals.length > 0) {
          goalUpdates.goal_progress = goals
            .map((g, i) => `Goal ${i + 1}: ${g.title}\n  Today's progress: \n  Notes: `)
            .join('\n\n');
          goalUpdates.shift_goals_focus = goals.map(g => g.title).join(', ');
        }
        if (trackedKm !== null && trackedKm > 0) {
          // Round to 1 decimal for readability; worker can still edit.
          goalUpdates.travel_kms = Math.round(trackedKm * 10) / 10;
        }
        if (Object.keys(goalUpdates).length > 0) {
          setFormData(prev => ({ ...prev, ...goalUpdates }));
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplate(template);
    // Preserve prefilled fields that survive across templates (travel km,
    // goals focus, etc.) so the worker doesn't lose the auto-tracked km
    // when they pick a template.
    setFormData(prev => ({
      ...(prev.travel_kms !== undefined ? { travel_kms: prev.travel_kms } : {}),
      ...(prev.shift_goals_focus ? { shift_goals_focus: prev.shift_goals_focus } : {}),
      ...(prev.goal_progress ? { goal_progress: prev.goal_progress } : {}),
    }));
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    // Clear missing-field highlight as soon as the user starts filling it
    if (missingFields.has(fieldName)) {
      setMissingFields(prev => {
        const next = new Set(prev);
        next.delete(fieldName);
        return next;
      });
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info('Recording started... Speak now');
    } catch (error) {
      toast.error('Failed to access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setTranscribing(true);
    try {
      const response = await voiceApi.transcribe(audioBlob);
      const transcribedText = response.data.text;
      setRawNotes(prev => prev + (prev ? '\n\n' : '') + transcribedText);
      toast.success('Audio transcribed successfully');
    } catch (error) {
      toast.error('Transcription failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTranscribing(false);
    }
  };

  const saveReport = async (generateAI = false) => {
    if (!selectedClient || !selectedTemplate) {
      toast.error('Please select a client and template');
      return;
    }

    setSaving(true);
    try {
      const reportData = {
        client_id: selectedClient,
        template_id: selectedTemplate.id,
        template_type: selectedTemplate.type,
        form_data: formData,
        raw_notes: rawNotes
      };

      let savedReport;
      if (report?.id) {
        savedReport = await reportsApi.update(report.id, reportData);
      } else {
        savedReport = await reportsApi.create(reportData);
      }
      
      setReport(savedReport.data);
      toast.success('Report saved');

      // Update URL to include report ID for new reports
      if (!report?.id && savedReport.data.id) {
        navigate(`/reports/${savedReport.data.id}`, { replace: true });
      }

      if (generateAI) {
        await generateAIReport(savedReport.data.id);
      }
    } catch (error) {
      toast.error('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const generateAIReport = async (reportId) => {
    setGenerating(true);
    try {
      const response = await reportsApi.generateAI(reportId || report.id);
      setReport(response.data);
      toast.success('AI report generated successfully!');
    } catch (error) {
      toast.error('AI generation failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const submitReport = async () => {
    if (!report?.id) {
      toast.error('Please save the report first');
      return;
    }

    // Validate required template fields before submission
    const missing = [];
    (selectedTemplate?.fields || []).forEach((f) => {
      if (!f.required) return;
      const v = formData[f.name];
      const isEmpty =
        v === undefined || v === null ||
        (typeof v === 'string' && v.trim() === '') ||
        (Array.isArray(v) && v.length === 0);
      if (isEmpty) missing.push({ name: f.name, label: f.label });
    });

    if (missing.length > 0) {
      setMissingFields(new Set(missing.map(m => m.name)));
      const labels = missing.map(m => m.label).slice(0, 3).join(', ');
      const more = missing.length > 3 ? ` + ${missing.length - 3} more` : '';
      toast.error(`Please fill in required field${missing.length !== 1 ? 's' : ''}: ${labels}${more}`);
      // Scroll the first missing field into view
      setTimeout(() => {
        const el = document.querySelector(`[data-field-wrapper="${missing[0].name}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setMissingFields(new Set());
    try {
      await reportsApi.submit(report.id, notifyEmail || null);
      toast.success('Report submitted successfully!' + (notifyEmail ? ' Notification email sent.' : ''));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    }
  };

  const handleDownloadPdf = async () => {
    if (!report?.id) {
      toast.error('Please save the report first');
      return;
    }
    try {
      const blob = await reportsApi.getPdfBlob(report.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${report.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSavePdf = async () => {
    if (!report?.id) {
      toast.error('Please save the report first');
      return;
    }
    setSavingPdf(true);
    try {
      await reportsApi.savePdf(report.id);
      toast.success('PDF saved permanently to archive');
    } catch (error) {
      toast.error('Failed to save PDF');
    } finally {
      setSavingPdf(false);
    }
  };

  const handlePhotoUpload = async (fieldName, files) => {
    if (!files || files.length === 0) return;
    setUploadingPhoto(true);
    const currentPhotos = formData[fieldName] || [];
    const newPhotos = [...currentPhotos];
    let okCount = 0;

    for (const file of Array.from(files)) {
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 25MB before conversion)`);
        continue;
      }
      try {
        // Convert HEIC → JPEG, downscale huge camera photos. JPEG/PNG <1.5MB pass through.
        const normalised = await normaliseImageForUpload(file);
        const res = await photosApi.upload(normalised);
        newPhotos.push({
          id: res.data.id,
          filename: normalised.name,
          size: normalised.size,
        });
        okCount += 1;
      } catch (err) {
        toast.error(err.message || `Failed to upload ${file.name}`);
      }
    }

    handleFieldChange(fieldName, newPhotos);
    if (okCount > 0) toast.success(`${okCount} photo${okCount !== 1 ? 's' : ''} uploaded`);
    setUploadingPhoto(false);
  };

  const removePhoto = (fieldName, photoIndex) => {
    const currentPhotos = formData[fieldName] || [];
    handleFieldChange(fieldName, currentPhotos.filter((_, i) => i !== photoIndex));
  };

  const loadVersions = async () => {
    if (!report?.id) return;
    try {
      const response = await reportsApi.getVersions(report.id);
      setVersions(response.data);
      setShowVersions(true);
    } catch (error) {
      toast.error('Failed to load version history');
    }
  };

  const restoreVersion = async (version) => {
    if (!report?.id) return;
    setRestoringVersion(true);
    try {
      const response = await reportsApi.restoreVersion(report.id, version);
      setReport(response.data);
      setFormData(response.data.form_data || {});
      setRawNotes(response.data.raw_notes || '');
      setShowVersions(false);
      setDiffVersion(null);
      toast.success(`Restored to version ${version}`);
      const versionsRes = await reportsApi.getVersions(report.id);
      setVersions(versionsRes.data);
    } catch (error) {
      toast.error('Failed to restore version');
    } finally {
      setRestoringVersion(false);
    }
  };

  const getFieldDiffs = (currentData, oldData) => {
    const allKeys = new Set([...Object.keys(currentData || {}), ...Object.keys(oldData || {})]);
    const diffs = [];
    allKeys.forEach(key => {
      const cur = JSON.stringify(currentData?.[key] ?? '');
      const old = JSON.stringify(oldData?.[key] ?? '');
      if (cur !== old) {
        diffs.push({
          field: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          current: typeof currentData?.[key] === 'object' ? JSON.stringify(currentData[key]) : String(currentData?.[key] ?? ''),
          previous: typeof oldData?.[key] === 'object' ? JSON.stringify(oldData[key]) : String(oldData?.[key] ?? ''),
        });
      }
    });
    return diffs;
  };

  const renderField = (field) => {
    const value = formData[field.name] || '';

    switch (field.type) {
      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="h-12"
            data-testid={`field-${field.name}`}
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder || ''}
            className="h-12"
            data-testid={`field-${field.name}`}
          />
        );
      case 'date': {
        // Compute this week's Monday (Sydney local) and the previous week's Monday
        const now = new Date();
        const day = (now.getDay() + 6) % 7; // Mon = 0
        const thisMon = new Date(now);
        thisMon.setDate(now.getDate() - day);
        thisMon.setHours(0, 0, 0, 0);
        const weekOffset = value ? (() => {
          try {
            const [yy, mm, dd] = value.split('-').map(Number);
            const picked = new Date(yy, mm - 1, dd);
            const pickedMon = new Date(picked);
            pickedMon.setDate(picked.getDate() - ((picked.getDay() + 6) % 7));
            pickedMon.setHours(0, 0, 0, 0);
            return Math.round((pickedMon - thisMon) / (7 * 86400000));
          } catch { return 0; }
        })() : 0;
        const [browserWeekOffset] = [weekOffset];
        const shownMon = new Date(thisMon);
        shownMon.setDate(thisMon.getDate() + browserWeekOffset * 7);
        const days = Array.from({ length: 7 }, (_, i) => {
          const d = new Date(shownMon);
          d.setDate(shownMon.getDate() + i);
          const pad = (x) => String(x).padStart(2, '0');
          const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          return { iso, dayName: ['MON','TUE','WED','THU','FRI','SAT','SUN'][i], dayNum: d.getDate() };
        });
        const todayIso = (() => {
          const t = new Date();
          const pad = (x) => String(x).padStart(2, '0');
          return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
        })();
        return (
          <div className="space-y-2" data-testid={`week-picker-${field.name}`}>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  const m = new Date(shownMon); m.setDate(m.getDate() - 7);
                  const pad = (x) => String(x).padStart(2, '0');
                  handleFieldChange(field.name, `${m.getFullYear()}-${pad(m.getMonth()+1)}-${pad(m.getDate())}`);
                }}
                className="px-2 h-9 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50"
                data-testid={`week-picker-prev-${field.name}`}
              >
                ← last week
              </button>
              {days.map(d => {
                const selected = value === d.iso;
                const isToday = d.iso === todayIso;
                return (
                  <button
                    key={d.iso}
                    type="button"
                    onClick={() => handleFieldChange(field.name, d.iso)}
                    className={`flex-1 min-w-[48px] h-9 rounded border text-xs font-medium transition ${
                      selected ? 'bg-teal-600 text-white border-teal-600' :
                      isToday ? 'bg-teal-50 border-teal-400 text-teal-700' :
                      'bg-white border-slate-300 text-slate-600 hover:border-teal-400'
                    }`}
                    data-testid={`week-picker-day-${field.name}-${d.iso}`}
                  >
                    <div>{d.dayName}</div>
                    <div className="text-[10px] opacity-80">{d.dayNum}</div>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => {
                  const m = new Date(shownMon); m.setDate(m.getDate() + 7);
                  const pad = (x) => String(x).padStart(2, '0');
                  handleFieldChange(field.name, `${m.getFullYear()}-${pad(m.getMonth()+1)}-${pad(m.getDate())}`);
                }}
                className="px-2 h-9 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50"
                data-testid={`week-picker-next-${field.name}`}
              >
                next week →
              </button>
            </div>
            <Input
              type="date"
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              className="h-12"
              data-testid={`field-${field.name}`}
            />
          </div>
        );
      }
      case 'time':
        return (
          <Input
            type="time"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="h-12"
            data-testid={`field-${field.name}`}
          />
        );
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder="Type here"
            rows={4}
            className="resize-y"
            data-testid={`field-${field.name}`}
          />
        );
      case 'select':
        return (
          <Select value={value} onValueChange={(v) => handleFieldChange(field.name, v)}>
            <SelectTrigger className="h-12" data-testid={`field-${field.name}`}>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2" data-testid={`field-${field.name}`}>
            {field.options?.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.name}-${option}`}
                  checked={selectedValues.includes(option)}
                  onCheckedChange={(checked) => {
                    const newValue = checked
                      ? [...selectedValues, option]
                      : selectedValues.filter(v => v !== option);
                    handleFieldChange(field.name, newValue);
                  }}
                />
                <label htmlFor={`${field.name}-${option}`} className="text-sm">{option}</label>
              </div>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <Checkbox
            checked={!!value}
            onCheckedChange={(checked) => handleFieldChange(field.name, checked)}
            data-testid={`field-${field.name}`}
          />
        );
      case 'duration_hm':
        const durVal = typeof value === 'object' && value !== null ? value : { hours: '', minutes: '' };
        return (
          <div className="flex items-center gap-3" data-testid={`field-${field.name}`}>
            <div className="flex items-center gap-2 flex-1">
              <Input
                type="number"
                min="0"
                max="24"
                placeholder="0"
                value={durVal.hours || ''}
                onChange={(e) => handleFieldChange(field.name, { ...durVal, hours: e.target.value })}
                className="h-12 w-24"
                data-testid={`field-${field.name}-hours`}
              />
              <span className="text-sm text-slate-500 font-medium">hrs</span>
            </div>
            <div className="flex items-center gap-2 flex-1">
              <Input
                type="number"
                min="0"
                max="59"
                placeholder="0"
                value={durVal.minutes || ''}
                onChange={(e) => handleFieldChange(field.name, { ...durVal, minutes: e.target.value })}
                className="h-12 w-24"
                data-testid={`field-${field.name}-minutes`}
              />
              <span className="text-sm text-slate-500 font-medium">min</span>
            </div>
          </div>
        );
      case 'signature':
        return (
          <div data-testid={`field-${field.name}`}>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-white">
              <Input
                value={value}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                placeholder="Type your full name as signature"
                className="h-12 border-0 border-b-2 border-slate-300 rounded-none text-center text-lg italic focus:border-teal-500 focus:ring-0"
                data-testid={`field-${field.name}-input`}
              />
              <p className="text-center text-xs text-slate-400 mt-2">By typing your name, you confirm this report is accurate</p>
            </div>
          </div>
        );
      case 'photo_upload':
        const photos = Array.isArray(value) ? value : [];
        return (
          <div data-testid={`field-${field.name}`}>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              className="hidden"
              onChange={(e) => handlePhotoUpload(field.name, e.target.files)}
            />
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="w-full h-14 border-dashed border-2 border-slate-300 hover:border-teal-500"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid={`upload-photo-btn-${field.name}`}
              >
                {uploadingPhoto ? (
                  <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Uploading...</>
                ) : (
                  <><Camera className="h-5 w-5 mr-2 text-slate-400" /> Choose Photos</>
                )}
              </Button>
              {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {photos.map((photo, idx) => (
                    <PhotoThumbnail
                      key={photo.id || idx}
                      photo={photo}
                      onRemove={() => removePhoto(field.name, idx)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      case 'medication_admin':
        const medSlots = field.med_slots || [];
        const medValues = typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {};
        return (
          <div className="space-y-3 bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid={`field-${field.name}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-6 w-6 bg-teal-600 rounded flex items-center justify-center">
                <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
              </div>
              <span className="text-sm font-semibold text-slate-700">Medication Administration</span>
            </div>
            {medSlots.length === 0 ? (
              <p className="text-sm text-slate-400 italic">No medication slots configured. Organisation can set these up in the Template Editor.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {medSlots.map((slot, slotIdx) => {
                  if (!slot.name && (!slot.options || slot.options.length === 0)) return null;
                  const slotKey = `med_${slotIdx}`;
                  return (
                    <div key={slotIdx} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`med-entry-${slotIdx}`}>
                      <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
                        {slot.name || `Medication ${slotIdx + 1}`}
                      </Label>
                      <Select
                        value={medValues[slotKey] || ''}
                        onValueChange={(v) => handleFieldChange(field.name, { ...medValues, [slotKey]: v })}
                      >
                        <SelectTrigger className="h-10" data-testid={`med-select-${slotIdx}`}>
                          <SelectValue placeholder="Select medication..." />
                        </SelectTrigger>
                        <SelectContent>
                          {(slot.options || []).map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                          <SelectItem value="not_administered">Not Administered</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="h-12"
            data-testid={`field-${field.name}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="report-writer">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <h1 className="font-heading font-semibold text-slate-900">
                {id ? 'Edit Report' : 'New Report'}
              </h1>
            </div>
            <LogoHome className="h-12 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client & Template Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Report Details</CardTitle>
                <CardDescription>Select the client and template for this report</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <Select value={selectedClient} onValueChange={setSelectedClient}>
                      <SelectTrigger className="h-12" data-testid="select-client">
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} ({client.ndis_number})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Template</Label>
                    <Select 
                      value={selectedTemplate?.id || ''} 
                      onValueChange={handleTemplateChange}
                    >
                      <SelectTrigger className="h-12" data-testid="select-template">
                        <SelectValue placeholder="Select a template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Template Fields */}
            {selectedTemplate && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">{selectedTemplate.name}</CardTitle>
                  <CardDescription className="space-y-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                      {selectedClient && <span><span className="font-medium text-slate-700">Client:</span> {selectedClient.name}</span>}
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {selectedTemplate.fields.map((field) => {
                    const isMissing = missingFields.has(field.name);
                    return (
                      <div
                        key={field.name}
                        data-field-wrapper={field.name}
                        className={`space-y-2 ${isMissing ? 'p-3 -mx-3 rounded-lg ring-2 ring-red-400 bg-red-50' : ''}`}
                        data-testid={`field-wrapper-${field.name}`}
                      >
                        <Label className="flex items-center gap-2 text-slate-900 font-medium">
                          {field.label}
                          {field.required && <span className="text-red-500">*</span>}
                          {isMissing && <span className="text-xs text-red-600 font-normal ml-auto">Required</span>}
                        </Label>
                        {field.placeholder && field.type === 'textarea' && (
                          <p className="text-sm text-slate-500 -mt-1">{field.placeholder}</p>
                        )}
                        {renderField(field)}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Voice Notes Section */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Mic className="h-5 w-5 text-teal-600" />
                  Additional Notes
                </CardTitle>
                <CardDescription>Add notes via voice or text</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={isRecording ? "destructive" : "outline"}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={transcribing}
                    className={`${isRecording ? 'animate-pulse-ring' : ''}`}
                    data-testid="voice-record-btn"
                  >
                    {transcribing ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : isRecording ? (
                      <MicOff className="h-5 w-5 mr-2" />
                    ) : (
                      <Mic className="h-5 w-5 mr-2" />
                    )}
                    {transcribing ? 'Transcribing...' : isRecording ? 'Stop Recording' : 'Start Recording'}
                  </Button>
                </div>
                <Textarea
                  placeholder="Type additional notes here or use voice recording..."
                  value={rawNotes}
                  onChange={(e) => setRawNotes(e.target.value)}
                  rows={6}
                  data-testid="raw-notes"
                />
              </CardContent>
              <CardFooter className="flex flex-wrap gap-3 justify-end border-t pt-6">
                <Button
                  variant="outline"
                  onClick={() => saveReport(false)}
                  disabled={saving}
                  data-testid="save-draft-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Draft
                </Button>
                <Button
                  onClick={() => saveReport(true)}
                  disabled={saving || generating}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="generate-ai-btn"
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Generate AI Report
                </Button>
              </CardFooter>
            </Card>
          </div>

          {/* AI Generated Report Section */}
          <div className="space-y-6">
            {/* Review Feedback Banner */}
            {report?.status === 'changes_requested' && (
              <Card className="border-amber-300 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800">Changes Requested</p>
                      {report.review_feedback && (
                        <p className="text-sm text-amber-700 mt-1">{report.review_feedback}</p>
                      )}
                      <p className="text-xs text-amber-600 mt-2">Please address the feedback and resubmit.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {report?.status === 'approved' && (
              <Card className="border-green-300 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-green-800">Report Approved</p>
                      <p className="text-sm text-green-700 mt-1">This report has been reviewed and approved by a supervisor.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {report?.ai_generated_report && (
              <>
                <Card className="border-teal-200 bg-teal-50/50">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-teal-600" />
                      AI-Generated Report
                    </CardTitle>
                    <Badge className="w-fit bg-teal-100 text-teal-700">NDIS Audit Ready</Badge>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-slate-700 text-sm leading-relaxed">
                        {report.ai_generated_report}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Compliance Score */}
                {report.compliance_score && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-heading text-base flex items-center gap-2">
                        <Shield className="h-5 w-5 text-slate-600" />
                        Compliance Score
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-2xl font-bold text-slate-900">{report.compliance_score}%</span>
                          <Badge variant={report.compliance_score >= 85 ? 'default' : 'secondary'}
                            className={report.compliance_score >= 85 ? 'bg-green-100 text-green-700' : ''}
                          >
                            {report.compliance_score >= 85 ? 'Excellent' : report.compliance_score >= 70 ? 'Good' : 'Needs Review'}
                          </Badge>
                        </div>
                        <Progress value={report.compliance_score} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Cultural Safety Notes */}
                {report.cultural_safety_notes?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-heading text-base">Cultural Safety Notes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {report.cultural_safety_notes.map((note, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                            {note}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Submit / PDF Actions - available once report is saved */}
            {report?.id && (
              <>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="send-notification"
                      checked={showEmailInput}
                      onCheckedChange={setShowEmailInput}
                    />
                    <label htmlFor="send-notification" className="text-sm text-slate-600 cursor-pointer">
                      Send email notification on submit
                    </label>
                  </div>
                  {showEmailInput && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400" />
                      <Input
                        type="email"
                        placeholder="Enter email address..."
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        className="h-10"
                        data-testid="notify-email-input"
                      />
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1 h-12"
                      onClick={handleDownloadPdf}
                      data-testid="download-pdf-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                    <Button
                      variant="outline"
                      className="h-12"
                      onClick={handleSavePdf}
                      disabled={savingPdf}
                      data-testid="save-pdf-btn"
                    >
                      {savingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Save PDF
                    </Button>
                    <Button
                      className="flex-1 h-12 bg-slate-900 hover:bg-slate-800"
                      onClick={submitReport}
                      data-testid="submit-report-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Submit Report
                    </Button>
                  </div>
                </div>

                {report?.version > 1 && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={loadVersions}
                    data-testid="view-history-btn"
                  >
                    <History className="h-4 w-4 mr-2" />
                    View Version History (v{report.version})
                  </Button>
                )}
              </>
            )}

            {/* Version History Panel */}
            {showVersions && versions.length > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <History className="h-5 w-5 text-slate-600" />
                    Version History
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => { setShowVersions(false); setDiffVersion(null); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {versions.map((v) => {
                      const isExpanded = diffVersion?.id === v.id;
                      const diffs = isExpanded ? getFieldDiffs(report?.form_data, v.form_data) : [];
                      return (
                        <div key={v.id} className="rounded-lg border border-slate-200 hover:border-teal-500/50 transition-colors overflow-hidden">
                          <div className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant="secondary">Version {v.version}</Badge>
                              <span className="text-xs text-slate-500">
                                {new Date(v.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            {v.compliance_score && (
                              <p className="text-xs text-slate-500 mb-2">Compliance: {v.compliance_score}%</p>
                            )}
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => setDiffVersion(isExpanded ? null : v)}
                                data-testid={`diff-btn-v${v.version}`}
                              >
                                {isExpanded ? 'Hide' : 'View'} Changes
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => restoreVersion(v.version)}
                                disabled={restoringVersion}
                              >
                                {restoringVersion ? (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                )}
                                Restore
                              </Button>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="border-t border-slate-200 bg-slate-50 p-3" data-testid="version-diff-panel">
                              {diffs.length > 0 ? (
                                <div className="space-y-2">
                                  <p className="text-xs font-medium text-slate-600 mb-2">
                                    {diffs.length} field{diffs.length !== 1 ? 's' : ''} changed vs current
                                  </p>
                                  {diffs.map((d, i) => (
                                    <div key={i} className="text-xs rounded bg-white border border-slate-200 p-2">
                                      <p className="font-medium text-slate-700 mb-1">{d.field}</p>
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <span className="text-red-500 font-medium">v{v.version}:</span>
                                          <p className="text-slate-500 mt-0.5 break-words line-clamp-3">{d.previous || '(empty)'}</p>
                                        </div>
                                        <div>
                                          <span className="text-emerald-500 font-medium">Current:</span>
                                          <p className="text-slate-700 mt-0.5 break-words line-clamp-3">{d.current || '(empty)'}</p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-slate-500 text-center py-2">No form data differences found</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {!report?.ai_generated_report && (
              <Card className="bg-slate-100 border-dashed">
                <CardContent className="py-12 text-center">
                  <Sparkles className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">
                    Fill out the form and click &ldquo;Generate AI Report&rdquo; to create an NDIS audit-ready document
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
